'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchOffers } from '@eliadmin/core/src/api/jobportal/offers-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { normalizeJobOfferSort } from '@eliadmin/core/src/utils/retired-list-sort';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useState, useEffect, useMemo } from 'react';
import { useJobportalOffersStore } from '../../stores/jobportal-offers-store';
import { buildJobOffersQuery as buildPublicJobOffersQuery } from '@eliadmin/core/src/utils/jobportal-offers-query';
import { JobOfferCardSmall } from './job-offer-card-small';

export type JobPortalOffersContainerProps = {
  showPagination?: boolean;
  showLoadMore?: boolean;
  paginationKey?: string;
};

export function JobPortalOffersContainer(props: JobPortalOffersContainerProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const customerId = useCustomerStore(s => s.me?.customer?.id);
  const searchParams = useSearchParams();
  const filter = normalizeJobOfferSort(useJobportalOffersStore(s => s.filter));
  const clearTrigger = useJobportalOffersStore(s => s.clearTrigger);
  const setFilter = useJobportalOffersStore(s => s.setFilter);

  const [currentPage] = useState(parseInt(searchParams.get('page') || '1'));

  const queryWithoutPage = useMemo(() => {
    if (!currentPage) return {};

    return { page: currentPage };
  }, [currentPage]);

  useEffect(() => {
    setFilter({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTrigger]);

  const queryClient = useQueryClient();
  const listingQueryKey = [
    CacheKey.JOBPPORTAL_OFFERS,
    'public-offers',
    'infinite',
    searchParams.toString(),
    currentPage,
    queryWithoutPage,
    clearTrigger,
    filter,
    locale,
    authToken ?? null,
    customerId ?? null,
  ];

  const {
    data: jobOffersQuery,
    fetchNextPage,
    hasNextPage,
  } = useSuspenseInfiniteQuery({
    queryKey: listingQueryKey,
    queryFn: ({ pageParam, signal }) =>
      fetchOffers({
        query: {
          ...queryWithoutPage,
          page: pageParam,
          ...buildPublicJobOffersQuery({
            ...filter,
            is_paid: !filter.dual_education ? true : undefined,
            expires_at: !filter.dual_education ? true : undefined,
          } as any),
          'order[]': ['desc', filter.order, 'desc'],
          'orderBy[]': ['topped_up_to', filter.orderBy, 'id'],
          search: filter.search?.length && filter.search.length > 2 ? filter.search : undefined,
          results: 30,
        },
        locale,
        authToken,
        signal,
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },

    initialPageParam: currentPage ? currentPage : 1,
  });

  //   useEffect(() => {
  //     setSalaryRanges(jobOffersQuery?.pages[0].salary_type);
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //   }, [jobOffersQuery]);

  const lastPage = jobOffersQuery?.pages[jobOffersQuery?.pages.length - 1];
  const flatList = jobOffersQuery?.pages.flatMap(page => page.data) ?? [];

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-6 !px-0 @container md:px-4 md:py-8',
        'ea-job-portal-offers-container',
      )}
    >
      {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}

      <div
        className={cn(
          'grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3',
          'ea-job-portal-offers-container__grid',
        )}
      >
        {flatList?.map((offer, i) => (
          <JobOfferCardSmall key={i} offer={offer} />
        ))}
      </div>

      {!!hasNextPage && props.showLoadMore && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-offers-container__load-more',
          )}
        >
          <Button variant="secondary" onClick={() => fetchNextPage()}>
            {t('text338')}
          </Button>
        </div>
      )}

      {props.showPagination && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-offers-container__pagination',
          )}
        >
          <Pagination
            links={lastPage?.meta.links}
            onPageClicked={page => {
              if (String(page) !== String(currentPage)) return;
              queryClient.resetQueries({
                queryKey: listingQueryKey,
                exact: true,
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

export function JobPortalOffersContainerSkeleton() {
  const array = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div
      className={cn('relative flex flex-col gap-6 @container', 'ea-job-portal-offers-container')}
    >
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5 sm:gap-y-8 lg:grid-cols-3',
          'ea-job-portal-offers-container__grid ea-job-portal-offers-container__grid--skeleton',
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className="min-h-[400px] min-w-[250px]" />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
